import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle } from "lucide-react";

const BADGE = { ready: "badge-available", needs_info: "badge-reserved", blocked: "badge-sold", excluded: "badge-neutral" };

// ID-052: editable preview of proposed variants — edits are re-validated server-side before creation
export function BulkPreviewTable({ preview, rows, setRows, busy, onRecheck, onCreate }) {
  const edit = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const dirty = rows.some((r, i) => r.sku !== preview.rows[i]?.sku || r.include !== preview.rows[i]?.include);
  const c = preview.counts;
  return (
    <div className="mt-8" data-testid="design-preview">
      <p className="text-sm text-charcoal" data-testid="design-preview-counts">
        {preview.rows.length} colors · <b>{c.ready}</b> ready · <b>{c.needs_info}</b> need information · <b>{c.blocked}</b> blocked · {c.excluded} excluded
      </p>
      {preview.spec_issues.length > 0 && (
        <div className="mt-3 space-y-1 rounded-md border border-status-reserved/40 bg-status-reserved/5 p-4" data-testid="design-spec-issues">
          {preview.spec_issues.map((t) => <p key={t} className="flex items-start gap-2 text-sm text-charcoal"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-reserved" />{t}</p>)}
        </div>
      )}
      <div className="mt-4 overflow-x-auto rounded-md border border-border bg-white">
        <Table data-testid="design-preview-table" className="min-w-[820px]">
          <TableHeader><TableRow><TableHead>Include</TableHead><TableHead>Color</TableHead><TableHead>SKU</TableHead><TableHead>Format</TableHead><TableHead>Attach to</TableHead><TableHead>Flags</TableHead><TableHead>Result</TableHead></TableRow></TableHeader>
          <TableBody>
            {preview.rows.map((r, i) => (
              <TableRow key={r.color_code} data-testid={`design-row-${r.color_code}`}>
                <TableCell><Checkbox checked={rows[i]?.include} onCheckedChange={(v) => edit(i, { include: !!v })} data-testid={`design-include-${r.color_code}`} /></TableCell>
                <TableCell className="text-sm">{r.color_code} {r.color_name}{r.us_name ? ` · ${r.us_name}` : ""}</TableCell>
                <TableCell><Input value={rows[i]?.sku || ""} onChange={(e) => edit(i, { sku: e.target.value.toUpperCase() })} className="h-8 w-28 font-mono text-xs" data-testid={`design-sku-${r.color_code}`} /></TableCell>
                <TableCell className="text-xs text-warmgrey">{preview.spec.format_in} · {preview.spec.presentation}</TableCell>
                <TableCell className="text-xs text-warmgrey">{r.product_name || "—"}</TableCell>
                <TableCell className="text-xs text-charcoal">{r.flags.length ? r.flags.join("; ") : "—"}</TableCell>
                <TableCell><span className={`badge-status ${BADGE[r.state]}`} data-testid={`design-state-${r.color_code}`}>{r.state.replace("_", " ")}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button variant="outline" disabled={busy} onClick={onRecheck} data-testid="design-recheck">{dirty ? "Re-check my edits" : "Re-check"}</Button>
        <Button disabled={busy || dirty || !preview.can_create} onClick={onCreate} className="bg-terracotta text-white hover:bg-terracotta-hover" data-testid="design-create">
          Create {c.ready} variants
        </Button>
        {dirty && <span className="text-xs text-status-reserved" data-testid="design-dirty-note">Re-check your edits before creating.</span>}
        {!dirty && !preview.can_create && <span className="text-xs text-warmgrey" data-testid="design-blocked-note">Fix the flagged items (or exclude blocked rows) and re-check to enable creation.</span>}
      </div>
    </div>
  );
}
